import { Location } from 'history';
import React, { useCallback, useEffect, useState } from 'react';
import { Prompt } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ModalLeave } from 'lib/components/modal/ModalLeave';

interface Props {
  when?: boolean;
  navigate?: (to: string | Location) => void;
  shouldBlockNavigation: (nextLocation: Location) => boolean;
  onConfirm?: Function;
  onDiscard?: Function;
  onStay?: Function;
  stay?: boolean;
  title?: string;
  text?: string;
  discardButtonText?: string;
  confirmButtonText?: string;
  showDiscardIcon?: boolean;
  showSaveIcon?: boolean;
  infoBoxText?: string;
  disableSave?: boolean;
  fromSharedFolder?: boolean;
}

const RouteLeavingGuard = ({
  when = false,
  navigate = () => {},
  shouldBlockNavigation,
  onConfirm = () => {},
  onDiscard = () => {},
  onStay = () => {},
  stay = false,
  title,
  text,
  discardButtonText,
  confirmButtonText,
  showDiscardIcon = true,
  showSaveIcon = true,
  infoBoxText,
  disableSave,
  fromSharedFolder,
}: Props) => {
  const { t } = useTranslation();

  const translatedTitle = title ?? t('Leave without saving changes?');
  const translatedText =
    text ?? t('Your updates will not be saved. This action can’t be undone.');
  const translatedDiscardButtonText = discardButtonText ?? t('Discard Changes');
  const translatedConfirmButtonText = confirmButtonText ?? t('Save Changes');

  const [modalVisible, setModalVisible] = useState(false);
  const [lastLocation, setLastLocation] = useState<Location | null>(null);
  const [confirmedNavigation, setConfirmedNavigation] = useState(false);
  const [loading, setLoading] = useState(false);

  const closeModal = useCallback(() => setModalVisible(false), []);

  const handleBlockedNavigation = useCallback(
    (nextLocation: Location): boolean => {
      // If already confirmed, allow the transition
      if (confirmedNavigation) return true;

      // Delegate decision to parent
      if (shouldBlockNavigation(nextLocation)) {
        setModalVisible(true);
        setLastLocation(nextLocation);
        return false; // block for now
      }

      return true; // allow
    },
    [confirmedNavigation, shouldBlockNavigation]
  );

  const handleConfirmNavigationClick = useCallback(async () => {
    setLoading(true);

    await onConfirm?.();
    setLoading(false);
    setModalVisible(false);
    setConfirmedNavigation(true);
  }, [onConfirm]);

  const handleDiscardNavigationClick = useCallback(() => {
    onDiscard();
    setModalVisible(false);
    setConfirmedNavigation(true);
  }, [onDiscard]);

  const handleStay = useCallback(() => {
    closeModal();
    onStay();
  }, [closeModal, onStay]);

  // Perform the deferred navigation once confirmed.
  useEffect(() => {
    if (confirmedNavigation && lastLocation) {
      // Push the *entire* Location (keeps ?search and #hash)
      navigate(lastLocation);
      // reset the flag so future navigations re-prompt if needed
      setConfirmedNavigation(false);
      setLastLocation(null);
    }
  }, [confirmedNavigation, lastLocation, navigate]);

  return (
    <>
      <Prompt when={when} message={handleBlockedNavigation} />

      <ModalLeave
        open={modalVisible}
        title={translatedTitle}
        text={translatedText}
        discardButtonText={translatedDiscardButtonText}
        confirmButtonText={translatedConfirmButtonText}
        showDiscardIcon={showDiscardIcon}
        showSaveIcon={showSaveIcon}
        handleModalClose={closeModal}
        onClickDiscardButton={handleDiscardNavigationClick}
        onClickConfirmButton={stay ? handleStay : handleConfirmNavigationClick}
        infoBoxText={infoBoxText}
        disableSave={disableSave}
        fromSharedFolder={fromSharedFolder}
        loading={loading}
      />
    </>
  );
};

export default RouteLeavingGuard;
